import { ActivatedRoute, Params, Routes } from '@angular/router';
import { Observable, switchMap, of } from 'rxjs';

export const resolvePathParam = (route: ActivatedRoute, paramName: string): Observable<string> => {
  return route.params.pipe(
    switchMap((params) => {
      if (!route) {
        throw new Error('ProjectId not found in path params');
      }
      if (params[paramName]) {
        return of(params[paramName]);
      } else if (route.parent) {
        return resolvePathParam(route.parent, paramName);
      } else {
        throw new Error('ProjectId not found in path params');
      }
    }),
  );
};

export const resolvePathParamSnapshot = (route: ActivatedRoute, paramName: string): string => {
  if (!route) {
    throw new Error('ProjectId not found in path params');
  }
  const param = route.snapshot.params[paramName];
  if (param) {
    return param;
  }
  return resolvePathParamSnapshot(route.parent!, paramName);
};

export const resolveProjectId = (route: ActivatedRoute) => resolvePathParam(route, 'projectId');
export const resolveProjectIdSnapshot = (route: ActivatedRoute) => resolvePathParamSnapshot(route, 'projectId');

export const routes: Routes = [
  {
    path: '',
    loadComponent: () => import('./pages/home-main-page/home-main-page.component').then((c) => c.HomeMainPageComponent),
    loadChildren: () => import('./pages/home-main-page/home-routing.module').then((c) => c.HomeRoutingModule),
  },
  {
    path: 'settings',
    loadComponent: () =>
      import('./layouts/main-layout/admin-main-page.component').then((c) => c.AdminMainPageComponent),
    loadChildren: () => import('./layouts/main-layout/admin-routing.module').then((c) => c.AdminRoutingModule),
  },
  {
    path: 'super-admin',
    loadComponent: () =>
      import('./layouts/main-layout/super-admin-main-page.component').then((c) => c.SuperAdminMainPageComponent),
    loadChildren: () =>
      import('./layouts/main-layout/super-admin-routing.module').then((c) => c.SuperAdminRoutingModule),
  },
  {
    path: 'personal-area',
    loadComponent: () =>
      import('./layouts/main-layout/personal-area-main-page.component').then((c) => c.PersonalAreaMainPageComponent),
    loadChildren: () =>
      import('./layouts/main-layout/personal-area-routing.module').then((c) => c.PersonalAreaRoutingModule),
  },
  {
    path: 'project/:projectId',
    data: { customIdSelector: (params?: Params) => params?.['projectId'] },
    loadComponent: () =>
      import('./layouts/main-layout/project-main-page.component').then((c) => c.ProjectMainPageComponent),
    loadChildren: () => import('./layouts/main-layout/project-routing.module').then((c) => c.ProjectRoutingModule),
  },
  {
    path: 'calendar',
    loadComponent: () =>
      import('./pages/calendar-main-page/calendar-main-page.component').then((c) => c.CalendarMainPageComponent),
    loadChildren: () =>
      import('./pages/calendar-main-page/calendar-routing.module').then((c) => c.CalendarRoutingModule),
  },
  {
    path: 'team-allocation',
    loadComponent: () =>
      import('./pages/team-allocation-main-page/team-allocation-main-page.component').then(
        (c) => c.TeamAllocationMainPageComponent,
      ),
    loadChildren: () =>
      import('./pages/team-allocation-main-page/team-allocation-routing.module').then(
        (c) => c.TeamAllocationRoutingModule,
      ),
  },
  {
    path: 'inventory',
    loadComponent: () =>
      import('./pages/inventory-main-page/inventory-main-page.component').then((c) => c.InventoryMainPageComponent),
    loadChildren: () =>
      import('./pages/inventory-main-page/inventory-routing.module').then((c) => c.InventoryRoutingModule),
  },
  {
    path: 'files',
    loadComponent: () =>
      import('./pages/files-main-page/files-main-page.component').then((c) => c.FilesMainPageComponent),
    loadChildren: () => import('./pages/files-main-page/files-routing.module').then((c) => c.FilesRoutingModule),
  },
  {
    path: 'ui-kit',
    loadComponent: () => import('./pages/ui-kit-page/ui-kit-page.component').then((c) => c.UIKitPageComponent),
    loadChildren: () => import('./pages/ui-kit-page/ui-kit-routing.module').then((c) => c.UiKitRoutingModule),
  },
  {
    path: 'not-found',
    loadComponent: () => import('./pages/not-found-page/not-found-page.component').then((c) => c.NotFoundPageComponent),
  },
  { path: 'project', redirectTo: '', pathMatch: 'full' },
  { path: '**', redirectTo: '/not-found', pathMatch: 'full' },
];
